import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { AppCheckboxField, AppInputField, AppSelectField } from '@/form/FormUI';
import { SelectOptionsEditor } from './SelectOptionsEditor';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { isAskableInfoType } from '@rock/questions';
import type { InfoType, InfoTypeId, SpaceId } from '@rock/types';
import { DimensionsSubTypeSchema, FieldDataTypeSchema, InfoTypeSchema, LengthUnitSchema } from '@rock/types';
import { type FC, useCallback, useState } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

interface InfoTypeFormProps {
    spaceId: SpaceId;
    loading: boolean;
    type: InfoType | null;
    onSave: (type: InfoType) => void;
    onDelete: () => void;
    onCancel: () => void;
}

const InfoTypeForm: FC<InfoTypeFormProps> = ({ spaceId, loading, type, onSave, onDelete, onCancel }) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const form = useForm({
        resolver: zodResolver(InfoTypeSchema),
        defaultValues: type || {
            object: 'info-type',
            schema: 1,
            spaceId,
            id: '' as InfoTypeId,
            title: '',
        },
    });

    const watchedFields = useWatch({ control: form.control });

    const onInvalid = useCallback((errors: FieldErrors) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSave, onInvalid)}>
                <div className="py-4">
                    <h6>Details</h6>
                    <div className="flex flex-col gap-4 items-stretch p-4">
                        <AppInputField
                            label="ID"
                            placeholder="Unique identifier (not editable after create)"
                            control={form.control}
                            name="id"
                            disabled={!!type}
                        />
                        <AppSelectField
                            label="Info Data Type"
                            control={form.control}
                            name="dataType"
                            options={Object.values(FieldDataTypeSchema.enum)}
                        />

                        {watchedFields.dataType === 'select' && (
                            <SelectOptionsEditor
                                form={form}
                                name="options"
                                storagePathPrefix={`spaces/${spaceId}/info-types/images`}
                            />
                        )}

                        {watchedFields.dataType === 'dimensions' && (
                            <AppSelectField
                                label="Dimensions Sub Type"
                                control={form.control}
                                name="dimensionsSubType"
                                options={Object.values(DimensionsSubTypeSchema.enum)}
                            />
                        )}

                        {watchedFields.dataType === 'dimensions' &&
                            watchedFields.dimensionsSubType &&
                            watchedFields.dimensionsSubType !== 'sqft' && (
                                <AppSelectField
                                    label="Default Length Selection"
                                    control={form.control}
                                    name="defaultLengthSelection"
                                    options={Object.values(LengthUnitSchema.enum)}
                                />
                            )}

                        {watchedFields.dataType === 'dimensions' &&
                            watchedFields.dimensionsSubType &&
                            watchedFields.dimensionsSubType !== 'length' &&
                            watchedFields.dimensionsSubType !== 'sqft' && (
                                <AppCheckboxField
                                    label="Render Calculated Value"
                                    control={form.control}
                                    name="renderCalculatedValue"
                                    description="When enabled, displays the calculated value (e.g., '20x30 ft' → '600 ft²') instead of the raw dimensions"
                                />
                            )}

                        <AppInputField label="Title" control={form.control} name="title" placeholder="Title" />

                        <AppInputField
                            label="Help Text"
                            control={form.control}
                            name="helpText"
                            placeholder="Help Text"
                        />

                        <AppInputField
                            label="Question"
                            control={form.control}
                            name="question"
                            placeholder="How a dashboard card asks it, e.g. Does your home have a pool?"
                        />

                        {isAskableInfoType(watchedFields) && (
                            <AskExistingHomesField
                                value={form.getValues('askExistingHomes')}
                                onChange={(value) => form.setValue('askExistingHomes', value, { shouldDirty: true })}
                            />
                        )}
                    </div>
                </div>
                <DialogFooter className="flex flex-row items-center justify-between sm:justify-between">
                    {type ? (
                        <Button
                            type="button"
                            variant="destructive"
                            className="float-left"
                            onClick={() => setShowDeleteConfirm(true)}
                            disabled={loading}
                        >
                            Delete
                        </Button>
                    ) : (
                        <div />
                    )}
                    <div className="flex flex-row items-center justify-center gap-2">
                        <Button variant="secondary" onClick={onCancel} disabled={loading} type="button">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                            {type ? 'Update Info Type' : 'Create Info Type'}
                        </Button>
                    </div>
                </DialogFooter>
            </form>
            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirm Delete: {type?.title}</AlertDialogTitle>
                        <AlertDialogDescription>
                            This cannot be undone. Are you sure you want to delete this Info Type?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="cursor-pointer" disabled={loading}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={onDelete} disabled={loading}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Form>
    );
};

export default InfoTypeForm;

/**
 * Whether homes already past this type's space are asked it by a dashboard card. Turn it on when a guide
 * starts asking the type; `when` narrows who is asked (HQL).
 */
const AskExistingHomesField: FC<{
    value: InfoType['askExistingHomes'];
    onChange: (value: InfoType['askExistingHomes']) => void;
}> = ({ value, onChange }) => (
    <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2">
            <Switch
                checked={!!value}
                onCheckedChange={(checked) => onChange(checked ? { since: new Date().toISOString() } : undefined)}
            />
            <Label>Ask homes already past this space</Label>
        </div>
        {value && (
            <Input
                placeholder="Only when (HQL), e.g. home[year-home-built-0 < 1980]"
                value={value.when ?? ''}
                onChange={(event) =>
                    onChange({ since: value.since, ...(event.target.value ? { when: event.target.value } : {}) })
                }
            />
        )}
    </div>
);
